// src/components/discover/DiscoverSettingsSheet.tsx
//
// Bottom sheet for Discover-related settings. Triggered from the
// LocationStatusBadge in the top-right of the app shell.
//
// Surface:
//   - Location status (live, with accuracy in meters)
//   - "Open settings to fix" button when permission denied
//   - Distance slider (0.5 - 50 km, log-ish scale via presets)
//   - Unit toggle km / mi
//   - Two presets: Walking (1km) / Day trip (10km)
//   - Refresh-on-commit: when the user releases the slider, fire
//     a single backend refresh so the new radius takes effect
//     immediately rather than waiting for the next natural refresh.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Slider,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close,
  GpsFixed,
  GpsNotFixed,
  GpsOff,
  DirectionsWalk,
  DirectionsCar,
} from '@mui/icons-material';
import { useLocation } from '../../hooks/useLocation';
import {
  useUserPreferences,
  RADIUS_MIN_KM,
  RADIUS_MAX_KM,
  kmToDisplay,
  displayToKm,
} from '../../hooks/useUserPreferences';
import {
  deriveLocationStatus,
  LocationStatus,
} from '../LocationStatusBadge';
import { useT } from '../../i18n/useT';

interface DiscoverSettingsSheetProps {
  open: boolean;
  onClose: () => void;
  /** Fired when the user commits a new radius (slider release). */
  onRadiusCommit?: (radiusKm: number) => void;
}

export const DiscoverSettingsSheet: React.FC<DiscoverSettingsSheetProps> = ({
  open,
  onClose,
  onRadiusCommit,
}) => {
  const t = useT();
  const { prefs, update } = useUserPreferences();
  const { location, loading, hasPermission, requestPermission } = useLocation();

  // Local slider state — updated on every drag, committed on release.
  // Keeps Firestore writes to one per slider gesture rather than
  // dozens during a drag.
  const [draftKm, setDraftKm] = useState(prefs.discoverRadiusKm);
  useEffect(() => {
    if (open) setDraftKm(prefs.discoverRadiusKm);
  }, [open, prefs.discoverRadiusKm]);

  const unit = prefs.distanceUnit;
  const accuracy = location?.accuracy;
  const hasFix = !!(location?.latitude && location?.longitude);
  const status: LocationStatus = deriveLocationStatus(
    loading,
    hasPermission,
    accuracy,
    hasFix
  );

  const draftDisplay = kmToDisplay(draftKm, unit);
  const minDisplay = kmToDisplay(RADIUS_MIN_KM, unit);
  const maxDisplay = kmToDisplay(RADIUS_MAX_KM, unit);

  const handleSliderChange = (_e: Event, value: number | number[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    setDraftKm(displayToKm(v, unit));
  };

  const handleSliderCommit = (
    _e: React.SyntheticEvent | Event,
    value: number | number[]
  ) => {
    const v = Array.isArray(value) ? value[0] : value;
    const km = displayToKm(v, unit);
    update({ discoverRadiusKm: km });
    onRadiusCommit?.(km);
  };

  const handleUnitChange = (
    _e: React.MouseEvent<HTMLElement>,
    newUnit: 'km' | 'mi' | null
  ) => {
    if (newUnit) update({ distanceUnit: newUnit });
  };

  const applyPreset = (km: number) => {
    setDraftKm(km);
    update({ discoverRadiusKm: km });
    onRadiusCommit?.(km);
  };

  const StatusIcon =
    status === 'precise' ? GpsFixed : status === 'coarse' ? GpsNotFixed : GpsOff;
  const statusColor =
    status === 'precise'
      ? 'success.main'
      : status === 'coarse'
      ? 'warning.main'
      : 'error.main';
  const statusText =
    status === 'precise'
      ? `Precise${accuracy ? ` · ±${Math.round(accuracy)}m` : ''}`
      : status === 'coarse'
      ? `Approximate${accuracy ? ` · ±${Math.round(accuracy)}m` : ''}`
      : status === 'loading'
      ? 'Getting your location…'
      : 'Location unavailable';

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '85vh',
        },
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            bgcolor: 'action.disabledBackground',
          }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pt: 1,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          
          {t("Discover settings")}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label={t("Close")}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ p: 2.5, overflowY: 'auto' }}>
        {/* Location status */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            mb: 1,
            display: 'block',
          }}
        >
          
          {t("Location")}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 1,
          }}
        >
          <StatusIcon sx={{ color: statusColor }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {statusText}
          </Typography>
        </Box>
        {status === 'none' && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              
              {t("Discover needs your location to find nearby places. If you denied permission, you'll need to enable it in your browser/device settings.")}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => requestPermission()}
            >
              
              {t("Try again")}
            </Button>
          </Box>
        )}
        {location && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 2, fontSize: '0.7rem', opacity: 0.7 }}
          >
            {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
          </Typography>
        )}

        <Divider sx={{ mb: 2.5 }} />

        {/* Distance preference */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
            }}
          >
            
            {t("Distance")}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={unit}
            onChange={handleUnitChange}
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25 } }}
          >
            <ToggleButton value="km">{t("km")}</ToggleButton>
            <ToggleButton value="mi">{t("mi")}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {draftDisplay < 1
            ? draftDisplay.toFixed(2)
            : draftDisplay < 10
            ? draftDisplay.toFixed(1)
            : Math.round(draftDisplay)}{' '}
          {unit}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          
          {t("Show places within this distance from where you are.")}
        </Typography>

        <Slider
          value={draftDisplay}
          min={minDisplay}
          max={maxDisplay}
          step={unit === 'mi' ? 0.5 : 0.5}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommit}
          sx={{ mb: 1 }}
          aria-label={t("Discover radius")}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {minDisplay < 1 ? minDisplay.toFixed(1) : minDisplay} {unit}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {Math.round(maxDisplay)} {unit}
          </Typography>
        </Box>

        {/* Preset chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            icon={<DirectionsWalk fontSize="small" />}
            label={t("Walking · 1 km")}
            onClick={() => applyPreset(1)}
            color={draftKm === 1 ? 'primary' : 'default'}
            variant={draftKm === 1 ? 'filled' : 'outlined'}
          />
          <Chip
            icon={<DirectionsCar fontSize="small" />}
            label={t("Day trip · 10 km")}
            onClick={() => applyPreset(10)}
            color={draftKm === 10 ? 'primary' : 'default'}
            variant={draftKm === 10 ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>
    </Drawer>
  );
};
